// src/workspace/tools.js
//
// THE WORKSPACE'S TOOLS, AS DATA, IN ONE PLACE.
//
// ── WHY THIS IS NOT INLINE IN THE LANDING PAGE ANY MORE ─────────────────────
//
// It was, and that was right while the landing was the only thing that needed
// it. Then the shell header needed a tool NAME, and the shell had it hardcoded:
//
//     WellFinder <span>Review</span>
//
// on every workspace page, including the Feature Adjudicator and including the
// landing, where no tool is open at all. That is the two-owners problem in its
// plainest form -- a label the landing derives and the header restates -- and
// the header lost.
//
// So the identity of a tool (what it is called, where it lives, who may see it)
// is here, and the things only one surface needs stay with that surface: the
// landing attaches each card's Body, because those are React components and the
// header has no use for them.
//
// ── ACCESS IS STILL PER-CARD, BY REALM ROLE ─────────────────────────────────
//
// `role` is read by the landing to decide which cards exist. It is NOT a
// security boundary -- the server gates every route -- but it is what stops a
// reviewer being shown a tool they cannot open. A tool any of several roles
// may open lists them as `roles`; a tool whose route also gates on the org
// names it as `orgId`. canSeeTool reads all three.

// The adjudication tenant. Mirrors DREAMHAMMER_ORG_ID in
// admin-api/lib/wellfinder/adjudication.js, as WellFinderReviewPage's does.
export const DREAMHAMMER_ORG_ID = "815da853-bff7-4198-a09d-3590b55ef39f";

export const TOOLS = Object.freeze([
  {
    id: "wellfinder",
    role: "wellfinder_reviewer",
    label: "WellFinder Review",
    description: "Adjudicate detections against the RRC registry.",
    to: "/workspace/review",
    ready: true,
    // RESUME BEATS RESTART. With a live claim the link drops the tier filter
    // entirely so it lands on what is already claimed; offering a fresh tier
    // alongside a live claim invites abandoning records that then sit claimed
    // until the TTL expires.
    search: (ctx) => (ctx.me?.claims?.length ? {} : { tier: ctx.suggestedTier }),
    stats: "/api/v1/wellfinder/stats",
    // The metrics must decide whether to OPEN it. Anything that would not change
    // that is decoration and is left off the card.
    metrics: (s) => {
      const pending = Object.values(s?.by_tier_status || {})
        .reduce((n, t) => n + (t?.pending || 0), 0);
      return [{ label: "records pending", value: pending }];
    },
  },
  {
    id: "feature_adjudicator",
    role: "feature_reviewer",
    label: "Feature Adjudicator",
    description: "Resolve observations entity resolution would not decide.",
    to: "/workspace/features",
    ready: true,
    stats: "/api/v1/features/adjudication/stats",
    metrics: (s) => [
      { label: "cells with work", value: s?.outstanding?.cells ?? 0 },
      { label: "deferrals", value: s?.outstanding?.deferrals ?? 0 },
      // Only shown when non-zero: "0 locked" does not change anyone's decision,
      // and a row of zeroes trains a reader to stop looking.
      ...(s?.cells_locked ? [{ label: "in progress", value: s.cells_locked }] : []),
    ],
  },
  {
    // routes/feature-recheck.mjs's gate, exactly: the DreamHammer org, then
    // either reviewer role (a well_head verdict takes either; any other class
    // takes feature_reviewer, which the route decides per item).
    id: "recheck",
    roles: ["wellfinder_reviewer", "feature_reviewer"],
    orgId: DREAMHAMMER_ORG_ID,
    label: "Re-check",
    description: "Confirm earlier wells on a new collect.",
    to: "/workspace/recheck",
    ready: true,
    stats: "/api/v1/features/recheck/stats",
    metrics: (s) => [
      { label: "cells with work", value: s?.outstanding?.cells ?? 0 },
      { label: "items", value: s?.outstanding?.items ?? 0 },
      ...(s?.cells_locked ? [{ label: "in progress", value: s.cells_locked }] : []),
    ],
  },
]);

/**
 * Whether a user may see a tool: one of its roles held and, when it names an
 * org, that org the effective one (useEffectiveOrg, so an impersonating admin
 * sees what the org sees). A null org is the pending window and fails closed.
 */
export function canSeeTool(tool, { roles, orgId } = {}) {
  const held = roles || [];
  if (!(tool.roles ?? [tool.role]).some((r) => held.includes(r))) return false;
  return !tool.orgId || orgId === tool.orgId;
}

/**
 * The tool a path belongs to, or null.
 *
 * PREFIX MATCH, LONGEST FIRST, because a tool owns its subtree:
 * /workspace/features/-7096/2310 is the Feature Adjudicator with a cell
 * selected, not an unknown page. Longest-first matters the moment one tool's
 * path is a prefix of another's, which no amount of care in naming prevents
 * forever.
 *
 * NULL FOR THE LANDING, and that is a real answer rather than a miss: at
 * /workspace no tool is open, so the header has no tool to name.
 */
export function toolForPath(pathname) {
  const p = String(pathname || "");
  return [...TOOLS]
    .sort((a, b) => b.to.length - a.to.length)
    .find((t) => p === t.to || p.startsWith(`${t.to}/`)) ?? null;
}
